// Editorial copy for /why/:slug and /resources/:slug.
// Each slug has a hero, two-column body, bullet list, and a CTA.
export const WHY_CONTENT = {
  'performance-method': {
    eyebrow: 'Performance method',
    title: 'How REVA turns property data into performance',
    intro: 'REVA is built on a single operating idea — every property, lease, and ticket is an event in a measurable performance loop. We ingest, score, recommend, and close the loop.',
    pillars: [
      { h: 'Operational telemetry', p: 'Every signal — vacancy, rent paid, ticket aged — is observable from one screen. No more weekly spreadsheets.' },
      { h: 'Embedded scoring',      p: 'Portfolio Health, Readiness, and Risk scores update in real time, not at month-end.' },
      { h: 'AI co-pilot',           p: 'Recommendations are tied to specific assets and SOPs — not generic dashboards.' },
      { h: 'Closed-loop actions',   p: 'Every recommendation routes to a workflow with audit trail. Performance is governed, not advised.' },
    ],
    cta: 'See the performance method in action',
  },
  'customer-experience': {
    eyebrow: 'Customer experience',
    title: 'Operators, owners, tenants, and vendors — one calm operating system',
    intro: 'A customer experience is only as good as its weakest portal. REVA ships six portals that share one auth, one identity, one audit trail.',
    pillars: [
      { h: 'Tenant',   p: 'Pay rent · raise tickets · access manuals · WhatsApp acknowledgment — mobile-first, AR-native.' },
      { h: 'Owner',    p: 'Executive cash flow · NOI · portfolio health score · embedded onboarding video and AI assistant.' },
      { h: 'Vendor',   p: 'SMS / WhatsApp OTP login · before-after photos · invoice upload · SLA clock.' },
      { h: 'Investor', p: 'Passwordless secure access · institutional reports · distribution history.' },
    ],
    cta: 'Tour the portals',
  },
  'customer-stories': {
    eyebrow: 'Customer stories',
    title: 'Customer stories — by design partner only',
    intro: "REVA is in active rollout with select Saudi PMCs and giga-project operators. We publish customer stories under signed agreement only — and never invent testimonials. Once partners have eight quarters of live data we'll start publishing case studies here.",
    pillars: [
      { h: 'No fabricated quotes', p: 'Every operator quote on a REVA page is verifiable. If it is not signed and verifiable, it is not on the page.' },
      { h: 'Design-partner program', p: 'A small cohort of operators co-build the platform with us. Spots are limited and curated.' },
      { h: 'Reference calls', p: 'Existing partners are happy to take reference calls under NDA — request via your account team.' },
    ],
    cta: 'Apply to be a design partner',
  },
  'customer-trust': {
    eyebrow: 'Customer trust',
    title: 'How we earn and keep trust',
    intro: 'Operators trust REVA with their most sensitive data — rent rolls, tenant identities, financial reports. Trust is a daily discipline, not a marketing claim.',
    pillars: [
      { h: 'Per-tenant isolation by default', p: 'No shared collections. No accidental cross-org data leaks. Sandbox environments NEVER touch real organizations.' },
      { h: 'Safe-delete governance',          p: 'No record disappears without an audit trail. Owners approve or restore every deletion within a 30-day window.' },
      { h: 'Subscription-driven access',      p: 'Modules a customer hasn\'t bought are not just hidden — they are blocked at the API.' },
      { h: 'Invitation-only',                 p: 'There is no public signup. Every account is created and audited by a Property Owner or Platform Owner.' },
    ],
    cta: 'See the trust & security details',
  },
  'saudi-market-fit': {
    eyebrow: 'Saudi market fit',
    title: 'Saudi-first, by construction',
    intro: 'REVA was not retrofitted for Arabic. It was built for KSA from the first line of code — Arabic typography, RTL, SAR ledger, Hijri awareness, ZATCA layout, Ejar-ready connectors.',
    pillars: [
      { h: 'Bilingual AR · EN with RTL',  p: 'IBM Plex Sans Arabic + bidi reshaping for every PDF, screen, and email.' },
      { h: 'SAR-native ledger',           p: 'All amounts, taxes, and reports default to SAR. No currency conversion side effects.' },
      { h: 'Hijri-aware calendar',        p: 'Lease dates, rent cycles, and reporting periods support Hijri side-by-side with Gregorian.' },
      { h: 'ZATCA invoice layout',        p: 'Draft VAT-compliant invoice structure ready, full ZATCA integration on the roadmap.' },
      { h: 'Board-quality Arabic PDFs',   p: 'Owner reports, investor packs, and tenant statements look right in both languages.' },
    ],
    cta: 'Read the Saudi market brief',
  },
};

export const RESOURCES_CONTENT = {
  blog: {
    eyebrow: 'Blog',
    title: 'Insights for Saudi property operators',
    intro: 'Quarterly deep-dives into Saudi real estate operations — vacancy benchmarks, ZATCA prep, mega-development readiness frameworks. Editorial calendar opens once we exit closed beta.',
    pillars: [
      { h: 'Editorial discipline', p: 'No SEO chum, no AI-generated filler. We publish when we have a real operator story to tell.' },
      { h: 'Saudi market data',    p: 'Our blog is anchored in the operations data of our design partners (anonymized, aggregated).' },
      { h: 'Engineering posts',    p: 'How REVA solved a particular Saudi-specific problem — Hijri rounding, Arabic PDF reshaping, ZATCA QR generation.' },
    ],
    cta: 'Subscribe for first-issue access',
  },
  events: {
    eyebrow: 'Events',
    title: 'Operator roundtables and mega-development briefings',
    intro: 'We host a small number of closed-door operator roundtables each year — invite-only, no press, no sponsors. Built for senior operators.',
    pillars: [
      { h: 'Riyadh operator roundtable',  p: 'A quarterly closed-door working session for senior portfolio operators. Hosted in central Riyadh.' },
      { h: 'Mega-development readiness briefings',     p: 'A two-hour session on operational readiness for giga-scale projects — handover, SOPs, staffing, AI assist.' },
      { h: 'Tenant experience workshop',  p: 'One day. Five operators. How to redesign tenant onboarding for a Saudi resident.' },
    ],
    cta: 'Request an invitation',
  },
  newsroom: {
    eyebrow: 'Newsroom',
    title: 'REVA in the press',
    intro: 'We have not opened press relations yet. When we launch the first wave of customer stories, this newsroom will track every named press mention with the source link.',
    pillars: [
      { h: 'No undisclosed press',  p: 'Every entry here is dated, named, and links to the original source — no anonymous quotes.' },
      { h: 'Press kit',             p: 'Logos, screenshots, and brand guidelines available on request to credentialed journalists.' },
      { h: 'Founder commentary',    p: 'Long-form pieces from the REVA team will appear here once the platform is GA in KSA.' },
    ],
    cta: 'Request the press kit',
  },
  contact: {
    eyebrow: 'Contact us',
    title: 'Get in touch',
    intro: 'REVA is invitation-only. The fastest path to access is a Book-a-Demo conversation with our team — we will assess fit, set up your isolated sandbox, and walk through the platform together.',
    pillars: [
      { h: 'Sales',     p: 'For Property Manager / Owner / Investment-Manager prospects — book a demo and a 30-minute fit call.' },
      { h: 'Partner',   p: 'Vendors, integrators, and resellers — write to partners@reva.sa with a one-paragraph fit summary.' },
      { h: 'Support',   p: 'Existing customers raise tickets from inside the product — your account team has SLA-tracked queues.' },
      { h: 'Press',     p: 'Credentialed journalists — write to press@reva.sa for the press kit and founder interviews.' },
    ],
    cta: 'Book a demo',
  },
  guides: {
    eyebrow: 'Guides & templates',
    title: 'Operator playbooks and templates',
    intro: 'A curated library of operator-grade playbooks. Free for our customers, available under NDA to design partners, published progressively to the public.',
    pillars: [
      { h: 'Property onboarding checklist',   p: '54 items across legal, financial, operational, and tenant-facing handover. PDF + editable copy.' },
      { h: 'Mega-development readiness scoring template',  p: 'The exact scoring rubric REVA uses behind the Operational Readiness Engine — covers SOPs, staffing, compliance, handover.' },
      { h: 'Vendor SLA template',             p: 'Saudi-localized service level agreement structure for maintenance vendors — Arabic + English.' },
      { h: 'Tenant welcome packet',           p: 'A bilingual move-in packet with QR-coded house rules, contact info, and maintenance request flow.' },
    ],
    cta: 'Request the full library',
  },
  reports: {
    eyebrow: 'Real estate reports',
    title: 'Saudi real estate operations reports',
    intro: 'Once our design-partner cohort produces eight quarters of live data, REVA will publish anonymized aggregate operational benchmarks — vacancy velocity, collections lag, maintenance cycle time, NOI variance.',
    pillars: [
      { h: 'Quarterly operational benchmark', p: 'How long does a vacant unit stay vacant by emirate, segment, and asset class? Real data, anonymized.' },
      { h: 'Maintenance cycle report',        p: 'Median time from tenant ticket to resolution by category and vendor tier.' },
      { h: 'NOI variance survey',             p: 'How forecast NOI compares to realized NOI across asset types in KSA.' },
      { h: 'Mega-development readiness index',       p: 'An index that measures handover and go-live readiness for giga-projects.' },
    ],
    cta: 'Subscribe to the report',
  },
};
